<template>
  <Teleport to="body">
  <div v-if="show"
    class="flex items-center justify-center inset-0 fixed bg-black/50 backdrop-blur-sm z-40 overflow-y-auto overscroll-contain py-6"
  >
    <div
      ref="modalPanel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="snmp-modal-title"
      tabindex="-1"
      class="glass-card z-50 p-6 w-[90%] max-w-3xl max-h-[80vh] relative my-auto flex flex-col focus:outline-none"
    >

      <h2 id="snmp-modal-title" class="text-lg font-semibold mb-4 text-gray-700 dark:text-gray-50">
        {{ showLinkButton ? 'Preview Global SNMP' : isEditing ? 'Edit SNMP Configuration' : 'Add SNMP Configuration' }}
      </h2>
      <!-- Operation Error Banner -->
      <div
        v-if="errorMessage"
        class="mb-4 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-200 px-4 py-3 rounded relative"
        role="alert"
      >
        <div class="flex items-center justify-between">
          <span class="block sm:inline">{{ errorMessage }}</span>
          <button
            @click="$emit('clear-error-message')"
            aria-label="Dismiss error"
            title="Dismiss error"
            class="text-red-500 hover:text-red-700 dark:text-red-300 dark:hover:text-red-100"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      <div
        v-if="readOnly && isOrgLevel"
        class="mb-4 bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded p-3"
      >
        <p class="text-sm text-blue-800 dark:text-blue-200">
          This is an Organisation SNMP configuration. Edits done at the organisation level.
        </p>
      </div>

      <!-- Delete Confirmation Overlay -->
      <div 
        v-if="showDeleteConfirm"
        class="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30 rounded-lg"
      >
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 m-4 max-w-md">
          <h3 class="text-lg font-semibold mb-3 text-gray-900 dark:text-white">
            Confirm Deletion
          </h3>
          <p class="text-gray-700 dark:text-gray-300 mb-4">
            Are you sure you want to delete this SNMP configuration? All walk OIDs will also be deleted. This action cannot be undone.
          </p>
          <div class="flex gap-2 justify-end">
            <button
              type="button"
              @click="showDeleteConfirm = false"
              class="btn bg-gray-300 text-gray-700 hover:bg-gray-400 transition duration-200"
            >
              Cancel
            </button>
            <button
              type="button"
              @click="confirmDelete"
              class="btn bg-red-600 text-white hover:bg-red-700 transition duration-200"
            >
              Yes, Delete
            </button>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 flex-1 min-h-0 overflow-y-auto px-1 -mx-1">

        <div class="flex flex-col">
            <label for="tech_type" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
                Technology Type<span class="text-red-500">*</span>:
            </label>
            
            <div class="relative">
                <input
                v-model="localSnmp.tech_type"
                type="text"
                id="tech_type"
                required
                :disabled="readOnly"
                placeholder="Select or type technology type"
                @focus="showTechTypeDropdown = true; clearFieldError('tech_type')"
                @blur="hideTechTypeDropdown"
                @input="filterTechTypes"
                :class="['block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:bg-gray-600 dark:text-white focus:outline-none sm:text-sm rounded-md',
                  errors['tech_type'] ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-indigo-500 focus:border-indigo-500'
                ]"
                />
                <FieldError :message="errors['tech_type']"/>
                
                <div
                v-show="showTechTypeDropdown && filteredTechTypes.length > 0"
                class="absolute w-full mt-1 max-h-60 overflow-y-auto bg-white dark:bg-gray-600 dark:text-gray-50 border border-gray-300 rounded shadow-lg z-10"
                >
                <div
                    v-for="type in filteredTechTypes"
                    :key="type"
                    class="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    @mousedown.prevent="selectTechType(type)"
                >
                    {{ type }}
                </div>
                <div 
                    v-if="localSnmp.tech_type && !techTypes.includes(localSnmp.tech_type)"
                    class="px-3 py-2 text-gray-500 dark:text-gray-400 italic border-t border-gray-300"
                >
                    Custom: "{{ localSnmp.tech_type }}"
                </div>
                </div>
            </div>
        </div>

        <!-- Community String -->
        <div class="flex flex-col">
          <label for="community_string" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
            Community String<span class="text-red-500">*</span>:
          </label>
          <input 
            v-model="localSnmp.community_string"
            id="community_string"
            required
            :disabled="readOnly"
            @focus="clearFieldError('community_string')"
            :class="['border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:ring-2', 
              errors['community_string'] ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
            ]"
          />
          <FieldError :message="errors['community_string']"/>
        </div>

        <!-- Scrape Interval (staff only) -->
        <div v-if="isStaff" class="flex flex-col">
          <label for="scrape_interval" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
            Scrape Interval<span class="text-red-500">*</span>:
          </label>
          <input
            v-model="localSnmp.scrape_interval"
            id="scrape_interval"
            type="number"
            min="1"
            required
            :disabled="readOnly"
            :class="['border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2',
              errors['scrape_interval'] ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
            ]"
            @focus="clearFieldError('scrape_interval')"
          />
          <FieldError :message="errors['scrape_interval']"/>
        </div>

        <!-- Scrape Timeout (staff only) -->
        <div v-if="isStaff" class="flex flex-col">
          <label for="scrape_timeout" class="mb-1 font-medium text-gray-700 dark:text-gray-50">
            Scrape Timeout<span class="text-red-500">*</span>:
          </label>
          <input
            v-model="localSnmp.scrape_timeout"
            id="scrape_timeout"
            type="number"
            min="1"
            required
            :disabled="readOnly"
            :class="['border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2',
              errors['scrape_timeout'] ? 'ring-2 ring-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
            ]"
            @focus="clearFieldError('scrape_timeout')"
          />
          <FieldError :message="errors['scrape_timeout']"/>
        </div>

        <!-- SNMP Walk OIDs Section -->
        <div class="col-span-2 space-y-2 border-t border-gray-300 dark:border-gray-600 pt-4 mt-4">
          <h3 class="text-base font-semibold text-gray-700 dark:text-gray-50">SNMP Walk OIDs</h3>

          <!-- Add new walk OID -->
          <div v-if="!readOnly" class="flex gap-2">
            <input
              v-model="newWalkOIDValue"
              type="text"
              placeholder="e.g., fgSysCpuUsage"
              :disabled="readOnly"
              class="flex-1 border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:text-white"
              @keyup.enter="addWalkOID"
              @focus="clearFieldError('walk_oid')"
            />
            <button
              v-if="!readOnly"
              type="button"
              @click="addWalkOID"
              class="btn bg-green-600 text-white hover:bg-green-700"
            >
              Add OID
            </button>
          </div>
          <FieldError :message="errors['walk_oid']"/>

          <!-- Display existing walk OIDs -->
          <div v-if="localWalkOIDs.length > 0" class="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
            <div
              v-for="(walk, idx) in localWalkOIDs"
              :key="walk.id || idx"
              class="flex items-center justify-between glass-card-lite rounded px-2 py-0.5"
            >
              <span class="text-sm">{{ walk.value }}</span>
              <button
              v-if="!readOnly"
                type="button"
                @click="removeWalkOID(idx)"
                aria-label="Remove walk OID"
                title="Remove walk OID"
                class="text-red-500 hover:text-red-700 dark:hover:text-red-400 hover:bg-transparent bg-transparent"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
          </div>

          <p v-else class="text-sm text-gray-500 dark:text-gray-400 italic">
            No walk OIDs added yet.
          </p>
        </div>

        <!-- SNMP Targets Section -->
        <div class="col-span-2 space-y-2 border-t border-gray-300 dark:border-gray-600 pt-4 mt-4">
          <h3 class="text-base font-semibold text-gray-700 dark:text-gray-50">SNMP Targets (Optional)</h3>
          
          <!-- Add new target -->
          <div v-if="!readOnly" class="space-y-2">
            <div class="flex items-start gap-2">
              <div class="flex-1">
                <input
                  v-model="newTargetName"
                  type="text"
                  placeholder="Target name (e.g., Main Router)"
                  :disabled="readOnly"
                  :class="['w-full border rounded px-3 py-1.5 focus:outline-none focus:ring-2 dark:bg-gray-600 dark:text-white',
                    errors['target_name'] ? 'border-red-500 ring-2 ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                  ]"
                  @keyup.enter="addTarget"
                  @focus="clearFieldError('target_name')"
                />
                <FieldError :message="errors['target_name']"/>
              </div>

              <div class="flex-1">
                <input
                  v-model="newTargetIP"
                  type="text"
                  placeholder="IP address (e.g., 192.168.1.1)"
                  :disabled="readOnly"
                  :class="['w-full border rounded px-3 py-1.5 focus:outline-none focus:ring-2 dark:bg-gray-600 dark:text-white',
                    errors['target_ip'] ? 'border-red-500 ring-2 ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                  ]"
                  @keyup.enter="addTarget"
                  @focus="clearFieldError('target_ip')"
                />
                <FieldError :message="errors['target_ip']"/>
              </div>

              <button
                v-if="!readOnly"
                type="button"
                @click="addTarget"
                class="btn bg-green-600 text-white hover:bg-green-700"
              >
                Add Target
              </button>
            </div>
          </div>

          <!-- Display existing targets -->
          <div v-if="localSnmpTargets.length > 0" class="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
            <div
              v-for="(target, idx) in localSnmpTargets"
              :key="idx"
              class="flex items-center justify-between glass-card-lite rounded px-2 py-0.5"
            >
              <div class="flex-1">
                <span class="text-sm font-medium">{{ target.name }}</span>
                <span class="text-xs text-gray-500 dark:text-gray-400 ml-2">{{ target.ip }}</span>
              </div>
              <button
                v-if="!readOnly"
                type="button"
                @click="removeTarget(idx)"
                aria-label="Remove target"
                title="Remove target"
                class="text-red-500 hover:text-red-700 dark:hover:text-red-400 hover:bg-transparent bg-transparent"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
          </div>

          <p v-else class="text-sm text-gray-500 dark:text-gray-400 italic">
            No targets added yet.
          </p>
        </div>

      </div>

      <!-- Action buttons (pinned footer) -->
      <div class="flex justify-between pt-4 mt-4 border-t shrink-0">
          <div class="flex gap-2">
            <!-- Link Button (for org SNMPs being previewed from site/probe) -->
            <button
              v-if="showLinkButton"
              type="button"
              @click="handleLink"
              class="btn bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition duration-200"
            >
              Link to {{ parentTypeName }}
            </button>

            <!-- Save Button (for editable SNMPs) -->
            <button
              v-if="!readOnly && !showLinkButton"
              type="button"
              @click="handleSave"
              class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200"
            >
              Save
            </button>

            <!-- Cancel Button -->
            <button
              type="button"
              @click="handleCancel"
              class="btn bg-gray-300 text-gray-700 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
            >
              Cancel
            </button>
          </div>
          
          <div>
            <!-- Delete Button (only for owned, editable SNMPs) -->
            <button
              v-if="isEditing && canDelete && !readOnly"
              type="button"
              @click="handleDelete"
              class="btn bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition duration-200"
            >
              Delete
            </button>
          </div>
        </div>
    </div>
  </div>
  </Teleport>
</template>

<script>
import FieldError from "@/components/FieldError.vue";
import { useStore } from "@/store";

export default {
  name: "SNMPConfigModal",
  components: {
    FieldError
  },
  emits: ['save', 'cancel', 'delete', 'link', 'clear-error', 'clear-all-errors', 'validation-errors', 'clear-error-message'],
  props: {
    show: {
      type: Boolean,
      required: true
    },
    snmpData: {
      type: Object,
      default: () => ({
        tech_type: "",
        community_string: "",
        scrape_interval: 60,
        scrape_timeout: 30,
        org: null,
        site: null,
        probe: null,
        id: null
      })
    },
    walkOIDs: {
      type: Array,
      default: () => []
    },
    isEditing: {
      type: Boolean,
      default: false
    },
    canDelete: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    techTypes: {
      type: Array,
      default: () => ['fortigate', 'cisco']
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    isOrgLevel: {
      type: Boolean,
      default: false
    },
    showLinkButton: {
      type: Boolean,
      default: false
    },
    errorMessage: {
      type: String,
      default: null
    },
    parentType: {
      type: String,
      default: 'site'
    },
    snmpTargets: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      localSnmp: { ...this.snmpData },
      localWalkOIDs: [...this.walkOIDs],
      newWalkOIDValue: "",
      showTechTypeDropdown: false,
      filteredTechTypes: [],
      showDeleteConfirm: false,
      localSnmpTargets: [...this.snmpTargets],
      newTargetName: "",
      newTargetIP: ""
    };
  },
  watch: {
    snmpData: {
      handler(newVal) {
        this.localSnmp = { ...newVal };
      },
      deep: true
    },
    walkOIDs: {
      handler(newVal) {
        this.localWalkOIDs = [...newVal];
      },
      deep: true
    },
    snmpTargets: {
      handler(newVal) {
        this.localSnmpTargets = [...newVal];
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    this.filteredTechTypes = [...this.techTypes];

    // Scroll-lock the page behind the modal, restoring on close.
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Close on Escape (close the delete confirmation first if it's open).
    window.addEventListener("keydown", this.handleKeydown);

    // Move focus into the dialog so keyboard/screen-reader users start inside it.
    this.$nextTick(() => {
      this.$refs.modalPanel?.focus();
    });
  },
  beforeUnmount() {
    document.body.style.overflow = this.previousBodyOverflow || "";
    window.removeEventListener("keydown", this.handleKeydown);
  },
  computed: {
    parentTypeName() {
      return this.parentType.charAt(0).toUpperCase() + this.parentType.slice(1);
    },
    isStaff() {
      return useStore.isStaff();
    }
  },
  methods: {

    clearFieldError(fieldName) {
      this.$emit('clear-error', fieldName);
    },

    addWalkOID() {
      if (!this.newWalkOIDValue.trim()) {
        this.$emit('validation-errors', { walk_oid: "Please enter a walk OID value" });
        return;
      }

      const isDuplicate = this.localWalkOIDs.some(
        walk => walk.value.toLowerCase() === this.newWalkOIDValue.trim().toLowerCase()
      );

      if (isDuplicate) {
        this.$emit('validation-errors', { walk_oid: "This walk OID already exists" })
        return;
      }
      this.$emit('clear-error', 'walk_oid');

      this.localWalkOIDs.push({
        value: this.newWalkOIDValue.trim(),
        snmp: this.localSnmp.id,
        org: null,
        probe: null,
        site: null,
        id: null
      });

      this.newWalkOIDValue = "";
    },
    removeWalkOID(index) {
      this.localWalkOIDs.splice(index, 1);
    },
    handleSave() {
      this.$emit('clear-all-errors');
      const validationErrors = {};
      if (!this.localSnmp.tech_type || !this.localSnmp.tech_type.trim()) {
        validationErrors.tech_type = "Technology type is required";
      }
      if (!this.localSnmp.community_string || !this.localSnmp.community_string.trim()) {
        validationErrors.community_string = "Community string is required";
      }
      // Scrape fields are only shown to staff; don't validate fields the user can't see/edit.
      if (this.isStaff) {
        if (!this.localSnmp.scrape_interval || this.localSnmp.scrape_interval < 1) {
          validationErrors.scrape_interval = "Scrape interval must be at least 1 second";
        }
        if (!this.localSnmp.scrape_timeout || this.localSnmp.scrape_timeout < 1) {
          validationErrors.scrape_timeout = "Scrape timeout must be at least 1 second";
        }
      }
      
      // If there are validation errors, emit them to parent
      if (Object.keys(validationErrors).length > 0) {
        this.$emit('validation-errors', validationErrors);
        return;
      }

      this.$emit('save', {
        snmp: this.localSnmp,
        walkOIDs: this.localWalkOIDs,
        snmpTargets: this.localSnmpTargets
      });
    },
    handleCancel() {
      this.showDeleteConfirm = false;
      this.$emit('cancel');
    },
    handleDelete() {
      this.showDeleteConfirm = true;
    },
    confirmDelete() {
      this.$emit('delete', this.localSnmp.id);
      this.showDeleteConfirm = false;
    },
    
    filterTechTypes() {
        if (!this.localSnmp.tech_type) {
        this.filteredTechTypes = [...this.techTypes];
        } else {
        this.filteredTechTypes = this.techTypes.filter(type =>
            type.toLowerCase().includes(this.localSnmp.tech_type.toLowerCase())
        );
        }
    },
    addTarget() {
      const name = this.newTargetName.trim();
      const ip = this.newTargetIP.trim();
      
      const errors = {}; // Collect all errors
      
      if (!name) {
        errors.target_name = "Name is required";
      }
      if (!ip) {
        errors.target_ip = "IP is required";
      }

      if(ip && !this.isValidIP(ip)){
        errors.target_ip = "Invalid IP address format"
      }
      
      // Check duplicates only if values exist
      if (name) {
        const dupName = this.localSnmpTargets.some(t => t.name.toLowerCase() === name.toLowerCase());
        if (dupName) {
          errors.target_name = `Name "${name}" already exists`;
        }
      }
      
      if (ip && this.isValidIP(ip)) {
        const dupIP = this.localSnmpTargets.some(t => t.ip === ip);
        if (dupIP) {
          errors.target_ip = `IP "${ip}" already exists`;
        }
      }
  
      // If any errors, emit them all at once
      if (Object.keys(errors).length > 0) {
        this.$emit('validation-errors', errors);
        return;
      }
      
      this.$emit('clear-error', 'target_name');
      this.$emit('clear-error', 'target_ip');
      this.localSnmpTargets.push({ name, ip });
      this.newTargetName = "";
      this.newTargetIP = "";
    },

    isValidIP(ip) {
      const ipv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      
      const ipv6Regex = /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|::)$/;
      
      return ipv4Regex.test(ip) || ipv6Regex.test(ip);
    },


    removeTarget(index) {
      this.localSnmpTargets.splice(index, 1);
    },
    selectTechType(type) {
        this.localSnmp.tech_type = type;
        this.showTechTypeDropdown = false;
    },
    hideTechTypeDropdown() {
        setTimeout(() => {
        this.showTechTypeDropdown = false;
        }, 200);
    },
    handleLink() {
      this.$emit('link', this.localSnmp.id);
    },
    handleKeydown(e) {
      if (e.key !== 'Escape') return;
      if (this.showDeleteConfirm) {
        this.showDeleteConfirm = false;
      } else {
        this.handleCancel();
      }
    }
  }
};
</script>